import { useState } from "react";
import { CalendarRange, SlidersHorizontal } from "lucide-react";
import type { AlternateDate } from "@/lib/types";
import ChangeFlightDialog from "./ChangeFlightDialog";

/* The subtle "{N} date options · Change" affordance for a direction (Feature 2).
   Renders a small chip when there's more than one date variant, plus a single
   "Change" control that opens the flight picker. Self-hides the chip when there
   are no alternates, but always offers "Change flight" so the user can re-pick.
   One clear affordance per direction — never a wall of options. */

interface DateOptionsControlProps {
  tripId: string;
  routeId: string;
  directionLabel: string; // "Outbound" / "Return"
  dateOptionsCount?: number;
  alternateDates?: AlternateDate[];
  currentFlightNumber?: string | null;
}

export default function DateOptionsControl({
  tripId,
  routeId,
  directionLabel,
  dateOptionsCount,
  alternateDates,
  currentFlightNumber,
}: DateOptionsControlProps) {
  const [open, setOpen] = useState(false);
  const count = dateOptionsCount ?? 1;
  const hasDateOptions = count > 1;

  return (
    <>
      <div className="flex flex-wrap items-center gap-1.5">
        {hasDateOptions && (
          <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
            <CalendarRange className="h-3 w-3" aria-hidden="true" />
            <span className="font-mono">{count}</span> date options
          </span>
        )}
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-1 rounded-full border border-dashed border-[hsl(var(--primary))]/50 px-2 py-0.5 text-[11px] font-medium text-[hsl(var(--accent-foreground))] transition-colors hover:bg-[hsl(var(--primary))]/[0.06]"
        >
          <SlidersHorizontal className="h-3 w-3" aria-hidden="true" />
          Change
        </button>
      </div>

      <ChangeFlightDialog
        open={open}
        onOpenChange={setOpen}
        tripId={tripId}
        routeId={routeId}
        directionLabel={directionLabel}
        currentFlightNumber={currentFlightNumber}
        alternateDates={alternateDates}
      />
    </>
  );
}
